import React, { useEffect, useMemo, useCallback, useState, useLayoutEffect, useRef, Fragment } from 'react';

import './ImageLibrary.scss'
import { clearNavTiles, closeImageLibraries, fetchSubTiles, removeFromNavTrace, setImageLibraryImageConsumed, setProgress, setSelectedSource, setSuccess, setUpThirdPartyCategory, setUpThirdPartyLibraries } from './redux/actions';
import { SelectionGrid } from './SelectionGrid';
import alertify from "./../libs/alert"
import { ImageLibraryProps } from './types/ImageLibraryProps';
import { useDispatch, useSelector } from 'react-redux';
import { LibrarySourceType, LibraryTraceCrumb, ThirdPartyLibraryState } from './types/ThirdPartyLibrary';
import { ACTIONS_CLEAR_ALL } from '../redux/reducers/images';
import { clearImages, setRepeatType } from '../redux/actions/parts';
import { useTranslation } from 'react-i18next';
import { SEGMENT_SETTINGS_UPDATE } from '../redux/reducers/fabric/actions';
import { addImagesToPart } from '../redux/actions/parts/addImage';
import { getAsFile, handleFiles } from '../redux/actions/parts/addImage.helpers';
import { ImageSearch } from './ImageSearch';
import { NavTrace } from './NavTrace';
import { setUploadedImgSrc } from '../redux/actions/product';
import { ImageFilter } from './ImageFilter';

export const OWN_LIBRARY = 'ownLibrary';

export const ImageLibrary = (props: ImageLibraryProps) => {

    const { copyrightCompliance=true,repeatType= 'DEFAULT_REPEAT',fabricSize, partId, imageUploadInProgress, onImageSelected, showBackButton=true, wizardScreen} = props

    const dispatch = useDispatch();

    const ref = useRef(undefined as any);

    const [filters, setFilters] = useState([] as {filterKey:string, filterValue:string}[]);

    const { t } = useTranslation();
    const [readyToUploadMode, setreadyToUploadMode] = useState(true)
    const [selectedFiles, setSelectedFiles] = useState(undefined as File[] | undefined);

    const minResolution = useSelector((state: any) => state.fbdata?.minimalResolution);
    const selectedSegment = useSelector((state: any) => state.fabric.segmentInFocus);
    const resolutionSetting = useSelector((state: any) => state.fbdata?.fabricAutoSizeImage);
    const isDefault = useSelector((state: any) => state.fbdata?.isDefault)

    const [uploadMode, setUploadMode] = useState(false);

    let sourceString = useSelector((state: any) => state.fbdata?.useThirdPartyImageLibrary) || OWN_LIBRARY
    let thirdPartyImageSource = sourceString.split(';')

    if(thirdPartyImageSource.length){
        if((thirdPartyImageSource[0] !== 'patternDesigns') && (thirdPartyImageSource[0] !== OWN_LIBRARY)){
            thirdPartyImageSource = ['patternDesigns']
        }
    }

    const sendToHost = useCallback((action, value=0) => {
        let url =
            window.location !== window.parent.location
                ? document.referrer
                : document.location.origin
    
        window.parent.postMessage(
            JSON.stringify({
                action,
                value
            }),
            url,
        )
      }, [window])

    useEffect(() => {
        if (window.parent) {
            sendToHost('SCROLL_TO_TOP');
            sendToHost('CALC_IFRAME_HEIGHT', 1000);
          }
    }, [])

    const uploadHandler = useCallback((compliant: boolean) => (e: any, scaleFactor: number=1, isDefault: boolean, isLibImage: boolean,isFromPatternDesigns:boolean) => {
        const validity = compliant;
        if (validity) {
            const files = e.target.files
            if (files.length === 0) {
                setreadyToUploadMode(true)
                e.preventDefault()
                return
            }

            handleFiles(files, true).then((imageExifData) => {
                const images = imageExifData.map((data) => ({
                    ...data, minResolution, fabricSize, repeatType: 'DEFAULT_REPEAT', scaleFactor, isDefault, isLibImage, resolutionSetting,
                    image_id: (data.file as any).id
                }));
                dispatch({ type: ACTIONS_CLEAR_ALL })
                dispatch(clearImages(true))
                dispatch(addImagesToPart(partId, images, scaleFactor || 1, t, true))
                dispatch(setUploadedImgSrc(false))
                
                dispatch(setRepeatType(partId, 'DEFAULT_REPEAT', true))
                dispatch({
                    type: SEGMENT_SETTINGS_UPDATE, payload: {
                        parentCanvas: selectedSegment,
                        repeatType: 'DEFAULT_REPEAT'
                    }
                })
                dispatch(setUploadedImgSrc(isFromPatternDesigns))
                dispatch(setImageLibraryImageConsumed())
                dispatch(closeImageLibraries(window.scrollY));
                props.wizardScreen && props.wizardScreen()
            }).catch((error) => {
                console.log(error);
                alertify.alert(t("editor.alert_warning_title1"), t("editor.alert.warning_msg1"), t("i18n.ok"), () => { })
            }).finally(() => {
                imageUploadInProgress(false)
            })
        } else {
            e.target.files?.length && setSelectedFiles(e.target.files)
        }
    }, [repeatType, copyrightCompliance, imageUploadInProgress, minResolution, fabricSize, partId, selectedSegment])

    const { source, libraries, rootCategory } = useSelector(({ thirdPartyLibrary }: { thirdPartyLibrary: ThirdPartyLibraryState }) => thirdPartyLibrary);
    const trace = source && libraries[source].trace || []
    const progressState = source && libraries[source].progressState

    useEffect(() => {
        if (thirdPartyImageSource && thirdPartyImageSource.length && (!trace || !trace.length)) {
            dispatch(setSelectedSource(thirdPartyImageSource[0]))
            if (rootCategory && (rootCategory.id !== 'evroot')) {
                dispatch(setUpThirdPartyCategory(thirdPartyImageSource[0], rootCategory.id, rootCategory.categoryName, rootCategory.isPatternContainer))
                rootCategory && dispatch(fetchSubTiles(thirdPartyImageSource[0] as LibrarySourceType, {
                    id: rootCategory.id,
                    isCategory: true,
                    isPatternContainer: rootCategory.isPatternContainer,
                    name: rootCategory.categoryName
                }, 0))
            } else{
                const sourceLib = thirdPartyImageSource[0]
                dispatch(setUpThirdPartyLibraries({
                    sources: thirdPartyImageSource, selectedSource: sourceLib, openLibrary: (crumb: LibraryTraceCrumb) => {
                        (sourceLib === OWN_LIBRARY) && dispatch(fetchSubTiles(sourceLib, crumb, 0))
                    }
                }))
            }
        } else if (trace && trace.length) {
            dispatch(fetchSubTiles(source as LibrarySourceType, trace[trace.length - 1], 0))
        }
    }, [...thirdPartyImageSource])

    const goBack = useCallback(() => {
        const crumb = trace?.length > 1 && trace[trace.length - 2]
        if (crumb) {
            dispatch(removeFromNavTrace(crumb))
            dispatch(fetchSubTiles(source as LibrarySourceType, crumb, 0))
        }else if(trace?.length && source){
            dispatch(setSuccess(source))
            dispatch(clearNavTiles())
            dispatch(setSelectedSource(source))
        }
    }, [trace, source])

    const tiles = useMemo(() => {
        let selectionImages: any[] = []
        if (source) {
            const { selectionGrid } = libraries[source] || {}
            selectionImages = (selectionGrid || []).map(libraryTile => ({
                name: libraryTile.name,
                isCategory: libraryTile.isCategory,
                id: libraryTile.id,
                containsPatterns: libraryTile.isPatternContainer,
                thumbnail: libraryTile.thumbnail?.source,
                url: libraryTile.url,
                filter: libraryTile.filter,
                isLastElement: libraryTile.isLastElement,
                isPatternImage:libraryTile.isPatternDesign||false,
                scaleFactor: libraryTile.scaleFactor
            }))
        }
        // resizeListener({ timeout: 2000 })
        return selectionImages
    }, [source, trace, libraries])

    return <div className='library-container'>
        <div className='library-header'>
            <div className='title-header'>
                {showBackButton?<button 
                    title={wizardScreen ? "Back to Fabric Selection" : "Back to Editor"}
                    className='library-back-btn'
                    onClick={() => {
                        wizardScreen && wizardScreen('reset')
                        dispatch(closeImageLibraries(null))
                    }} ></button>:null}
                <div className='title'>{t("editor.custom.choose-tiles")}</div>
            </div>
            <div className='library-header_container'>
                {(sourceString !== OWN_LIBRARY)?<span>Tile Search:</span>: null}
                {(sourceString !== OWN_LIBRARY)?<ImageSearch source={source} />:<ImageFilter setFiltersToSearch={setFilters}/>}
                {source === 'patternDesigns_searchResults' ? <button className='back-to-library-home-btn'
                        title='Back to Library home' 
                        onClick={()=>{
                    dispatch(setSelectedSource(thirdPartyImageSource[0]))
                }}><img style={{width:'20px', height:'20px'}}
                        src='./img/icons/customeditor/Library_icon.svg'/>
                </button> : null}
        </div>
        </div>

        {source && (sourceString !== OWN_LIBRARY) ? <NavTrace
            source={source}
            thirdPartyImageSource={thirdPartyImageSource}
            trace={trace}
            rootCategory={rootCategory}
        />:null}

        <div className='library-container_categories' >
            <SelectionGrid wizardScreen={props.wizardScreen} 
                filters={filters} 
                showUploadTile={sourceString === OWN_LIBRARY} 
                goBack={goBack} 
                progressState={progressState} 
                crumb={trace && trace[trace.length - 1]} 
                ref={ref} 
                uploadImage={(url, id, name, isPatternDesign, scaleFactor) => {
                    setUploadMode(true)
                    imageUploadInProgress(true)
                    getAsFile(url, name).then((imagefile)=>{
                        let isLibImage = true;
                        (imagefile as any).id = `${id}`
                        //Guy scale factor 5
                        //uploadHandler(true)({ target: { files: [imagefile] } }, 1, isDefault, isLibImage,isPatternDesign)
                        uploadHandler(true)({ target: { files: [imagefile] } }, scaleFactor||1, isDefault, isLibImage,isPatternDesign)
                    }).catch((error)=>{
                        alertify.alert('Error',error.message,'OK')
                        imageUploadInProgress(false)
                    })
                }} 
                tiles={tiles} 
                fetchPatterns={(crumb, pageNumber) => {
                    dispatch(fetchSubTiles(source as LibrarySourceType, crumb, pageNumber))
                }} />
        </div>
    </div>
}

